import React, { useEffect, useState } from 'react';
import Header from "../Header";
import Sidebar from "./SideBar";
import Content from '../Content';
import authStorage from '../../utilities/Storage';
import axios from 'axios';
import { motion } from 'framer-motion';
import { 
  FiCalendar, FiClock, FiUsers, FiVideo, FiVideoOff, FiSearch, FiFilter, FiPlus,
  FiRefreshCw, FiEye, FiXCircle, FiChevronLeft, FiChevronRight, FiTrendingUp, FiMail,
  FiUser, FiUserCheck
} from 'react-icons/fi';

export default function MeetingsPage() {
  const [email, setEmail] = useState(null);
  const [company_name, setCompany_Name] = useState(null);
  const [loading, setLoading] = useState(true);
  const [meetings, setMeetings] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [hostFilter, setHostFilter] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [refreshing, setRefreshing] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [selectedMeeting, setSelectedMeeting] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [error, setError] = useState(null);
  const [meetingTitle, setMeetingTitle] = useState('');
  const [meetingDate, setMeetingDate] = useState('');
  const [meetingTime, setMeetingTime] = useState('');
  const [meetingDuration, setMeetingDuration] = useState('1 hour');
  const [meetingParticipants, setMeetingParticipants] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [meetingParticipantsData, setMeetingParticipantsData] = useState({});
  const [meetingStatus, setMeetingStatus] = useState('scheduled');
  const [showAllMeetings, setShowAllMeetings] = useState(false); // Toggle for admin view
  const [activeTab, setActiveTab] = useState('all'); // New state for tab filtering

  // API base URL
  const API_URL = 'https://backend-livestream-production.up.railway.app/api';

  // Fetch meetings data from your API
  const fetchMeetings = async () => {
    try {
      setRefreshing(true);
      setError(null);
      
      // Get user email from storage
      const userData = authStorage.getUser();
      let userEmail = null;
      
      if (userData) {
        try {
          const user = JSON.parse(userData);
          userEmail = user.email;
        } catch (error) {
          console.error("Failed to parse user data:", error);
        }
      }
      
      if (!userEmail) {
        throw new Error('User email not found. Please log in again.');
      }
      
      // Use different endpoint based on showAllMeetings toggle
      const endpoint = showAllMeetings ? `${API_URL}/admin/all-meetings` : `${API_URL}/meetings`;
      const params = showAllMeetings ? {} : { email: userEmail };
      
      // Call your API to get meetings
      const response = await axios.get(endpoint, { params });
      
      // Handle the response
      if (response.data && response.data.success) {
        // Format the meetings data
        const formattedMeetings = response.data.meetings.map(meeting => {
          // Use the status from the API response (backend now determines status)
          let status = meeting.status || 'scheduled';
          
          // Extract date and time from created_at
          const meetingDateTime = new Date(meeting.created_at);
          const date = meetingDateTime.toLocaleDateString();
          const time = meetingDateTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
          
          return {
            id: meeting.id,
            title: meeting.meeting_name,
            host: meeting.created_by,
            date: date,
            time: time,
            status: status,
            duration: '1h',
            participants: 0,
            roomName: meeting.room_name,
            meetingLink: meeting.meeting_link,
            createdAt: meeting.created_at,
            participantsList: meeting.participants || []
          };
        });
        
        // Fetch participants for each meeting
        const participantsPromises = formattedMeetings.map(async (meeting) => {
          try {
            const participantsResponse = await axios.get(`${API_URL}/meetings/${meeting.roomName}/participants`);
            if (participantsResponse.data && participantsResponse.data.success) {
              return {
                meetingId: meeting.id,
                participants: participantsResponse.data.participants || []
              };
            }
            return { meetingId: meeting.id, participants: [] };
          } catch (error) {
            console.error(`Error fetching participants for meeting ${meeting.id}:`, error);
            return { meetingId: meeting.id, participants: [] };
          }
        });
        
        const participantsResults = await Promise.all(participantsPromises);
        
        // Create a map of meeting ID to participants
        const participantsMap = {};
        participantsResults.forEach(result => {
          participantsMap[result.meetingId] = result.participants;
        });
        
        // Update meetings with participant count
        const meetingsWithParticipants = formattedMeetings.map(meeting => ({
          ...meeting,
          participants: participantsMap[meeting.id]?.length || 0
        }));
        
        setMeetingParticipantsData(participantsMap);
        setMeetings(meetingsWithParticipants);
        setTotalPages(Math.ceil(meetingsWithParticipants.length / 10));
      } else {
        setMeetings([]);
        setTotalPages(1);
      }
    } catch (error) {
      console.error('Error fetching meetings:', error);
      setError(error.message || 'Failed to fetch meetings');
      setMeetings([]);
      setTotalPages(1);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  // Create a new meeting
  const createMeeting = async () => {
    try {
      setSubmitting(true);
      setError(null);
      
      const userData = authStorage.getUser();
      let userEmail = null;
      
      if (userData) {
        try {
          const user = JSON.parse(userData);
          userEmail = user.email;
        } catch (error) {
          console.error("Failed to parse user data:", error);
        }
      }
      
      if (!userEmail) {
        throw new Error('User email not found. Please log in again.');
      }
      
      if (!meetingTitle.trim()) {
        throw new Error('Meeting title is required');
      }
      
      let participants = [];
      if (meetingParticipants.trim()) {
        participants = meetingParticipants.split(',').map(p => p.trim()).filter(p => p);
      }
      
      const response = await axios.post(`${API_URL}/meetings`, {
        email: userEmail,
        meetingName: meetingTitle.trim(),
        participants: participants.length > 0 ? participants : null,
        status: meetingStatus
      });
      
      if (response.data && response.data.success) {
        const newMeeting = {
          id: response.data.meeting_id,
          title: meetingTitle,
          host: userEmail,
          date: new Date().toLocaleDateString(),
          time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
          status: meetingStatus,
          duration: meetingDuration,
          participants: participants.length + 1,
          roomName: response.data.roomName,
          meetingLink: response.data.meetingLink,
          createdAt: new Date().toISOString(),
          participantsList: participants
        };
        
        setMeetings([newMeeting, ...meetings]);
        setTotalPages(Math.ceil((meetings.length + 1) / 10));
        
        setMeetingTitle('');
        setMeetingDate('');
        setMeetingTime('');
        setMeetingDuration('1 hour');
        setMeetingParticipants('');
        setMeetingStatus('scheduled');
        setShowScheduleModal(false);
        
        alert('Meeting scheduled successfully! Meeting link: ' + response.data.meetingLink);
      } else {
        throw new Error(response.data?.message || 'Failed to create meeting');
      }
    } catch (error) {
      console.error('Error creating meeting:', error);
      setError(error.message || 'Failed to create meeting');
      alert(error.message || 'Failed to create meeting');
    } finally {
      setSubmitting(false);
    }
  };

  // Get meeting details by room name
  const getMeetingDetails = async (roomName) => {
    try {
      setError(null);
      
      const response = await axios.get(`${API_URL}/meetings/${roomName}`);
      
      if (response.data && response.data.success) {
        return response.data.meeting;
      } else {
        throw new Error(response.data?.message || 'Meeting not found');
      }
    } catch (error) {
      console.error('Error fetching meeting details:', error);
      setError(error.message || 'Failed to fetch meeting details');
      return null;
    }
  };

  // Get meeting participants
  const getMeetingParticipants = async (roomName) => {
    try {
      setError(null);
      
      const response = await axios.get(`${API_URL}/meetings/${roomName}/participants`);
      
      if (response.data && response.data.success) {
        return response.data.participants;
      } else {
        throw new Error(response.data?.message || 'Failed to fetch meeting participants');
      }
    } catch (error) {
      console.error('Error fetching meeting participants:', error);
      setError(error.message || 'Failed to fetch meeting participants');
      return [];
    }
  };

  // Update meeting status
  const updateMeetingStatus = async (roomName, status) => {
    try {
      setError(null);
      
      const response = await axios.put(`${API_URL}/meetings/${roomName}/status`, {
        status: status
      });
      
      if (response.data && response.data.success) {
        setMeetings(meetings.map(m => 
          m.roomName === roomName ? { ...m, status: status } : m
        ));
        return true;
      } else {
        throw new Error(response.data?.message || 'Failed to update meeting status');
      }
    } catch (error) {
      console.error('Error updating meeting status:', error);
      setError(error.message || 'Failed to update meeting status');
      return false;
    }
  };

  function getUserRegistrationDetails() {
    const userData = authStorage.getUser();
    if (!userData) return;
    
    try {
      const user = JSON.parse(userData);
      setEmail(user.email);
      setCompany_Name(user.company_name);
    } catch (error) {
      console.error("Failed to parse user data:", error);
      setLoading(false);
    }
  }

  useEffect(() => {
    getUserRegistrationDetails();
    fetchMeetings();
  }, [showAllMeetings]); // Refetch when toggle changes

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const handleRefresh = () => {
    fetchMeetings();
  };

  const handleViewMeeting = async (meeting) => {
    const detailedMeeting = await getMeetingDetails(meeting.roomName);
    const participants = await getMeetingParticipants(meeting.roomName);
    
    if (detailedMeeting || participants) {
      setSelectedMeeting({
        ...meeting,
        ...detailedMeeting,
        participantsList: participants
      });
    } else {
      setSelectedMeeting(meeting);
    }
    
    setShowViewModal(true);
  };

  const handleJoinMeeting = (meeting) => {
    if (meeting.meetingLink) {
      updateMeetingStatus(meeting.roomName, 'ongoing');
      window.open(meeting.meetingLink, '_blank');
    } else {
      alert('Meeting link not available');
    }
  };

  const handleCancelMeeting = async (meeting) => {
    if (window.confirm('Are you sure you want to cancel this meeting?')) {
      const success = await updateMeetingStatus(meeting.roomName, 'cancelled');
      
      if (success) {
        alert('Meeting cancelled successfully');
      } else {
        alert('Failed to cancel meeting');
      }
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'ongoing':
        return <span className="badge bg-success bg-opacity-10 text-success animate-pulse"><FiVideo className="me-1" /> Live</span>;
      case 'upcoming':
        return <span className="badge bg-primary bg-opacity-10 text-primary"><FiCalendar className="me-1" /> Upcoming</span>;
      case 'completed':
        return <span className="badge bg-secondary bg-opacity-10 text-secondary"><FiVideoOff className="me-1" /> Completed</span>;
      case 'cancelled':
        return <span className="badge bg-danger bg-opacity-10 text-danger"><FiXCircle className="me-1" /> Cancelled</span>;
      case 'scheduled':
        return <span className="badge bg-info bg-opacity-10 text-info"><FiCalendar className="me-1" /> Scheduled</span>;
      default:
        return <span className="badge bg-secondary bg-opacity-10 text-secondary">Unknown</span>;
    }
  };

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1 }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { type: "spring", stiffness: 100 }
    }
  };

  // Filter meetings based on active tab
  const getFilteredMeetings = () => {
    let filtered = meetings;
    
    // Apply tab filter
    if (activeTab === 'active') {
      filtered = filtered.filter(meeting => 
        meeting.status === 'ongoing' || meeting.status === 'scheduled'
      );
    } else if (activeTab === 'past') {
      filtered = filtered.filter(meeting => 
        meeting.status === 'completed' || meeting.status === 'cancelled'
      );
    }
    
    // Apply search filter
    filtered = filtered.filter(meeting => {
      const matchesSearch = meeting.title.toLowerCase().includes(searchTerm.toLowerCase()) || 
                           meeting.host.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesStatus = filterStatus === 'all' || meeting.status === filterStatus;
      const matchesHost = !showAllMeetings || !hostFilter || meeting.host.toLowerCase().includes(hostFilter.toLowerCase());
      return matchesSearch && matchesStatus && matchesHost;
    });
    
    return filtered;
  };

  // Get filtered meetings
  const filteredMeetings = getFilteredMeetings();

  // Pagination
  const indexOfLastMeeting = currentPage * 10;
  const indexOfFirstMeeting = indexOfLastMeeting - 10;
  const currentMeetings = filteredMeetings.slice(indexOfFirstMeeting, indexOfLastMeeting);

  return (
    <div className="app">
      {/* Mobile Header */}
      <div className="d-md-none mobile-header bg-white shadow-sm p-3 d-flex justify-content-between align-items-center">
        <button className="btn btn-outline-secondary" onClick={toggleMobileMenu}>
          <i className="bi bi-list"></i>
        </button>
        <h5 className="mb-0 fw-bold">Meetings</h5>
        <button className="btn btn-outline-secondary" onClick={handleRefresh}>
          {refreshing ? <div className="spinner-border spinner-border-sm" role="status"><span className="visually-hidden">Loading...</span></div> : <FiRefreshCw />}
        </button>
      </div>

      {/* Sidebar with mobile toggle */}
      <div className={`sidebar-wrapper ${mobileMenuOpen ? 'mobile-open' : ''}`}>
        <Sidebar />
      </div>

      {/* Overlay for mobile menu */}
      {mobileMenuOpen && <div className="mobile-menu-overlay" onClick={toggleMobileMenu}></div>}

      <div className="main-content">
        <div className="d-none d-md-block"><Header /></div>
        <Content>
          <motion.div className="container-fluid py-4" initial="hidden" animate="visible" variants={containerVariants}>
            {/* Page Header */}
            <motion.div variants={itemVariants} className="mb-4">
              <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                  <h2 className="fw-bold mb-1">
                    {showAllMeetings ? 'All Meetings' : 'My Meetings'}
                  </h2>
                  <p className="text-muted mb-0">
                    {showAllMeetings ? 'View all meetings in the system.' : 'Manage and track your meetings.'}
                  </p>
                </div>
                <div className="d-flex gap-2">
                  {/* Toggle button for admin view */}
                  <button 
                    className={`btn ${showAllMeetings ? 'btn-primary' : 'btn-outline-primary'}`} 
                    onClick={() => setShowAllMeetings(!showAllMeetings)}
                  >
                    {showAllMeetings ? 'My Meetings' : 'View All'}
                  </button>
                  <button className="btn btn-primary" onClick={() => setShowScheduleModal(true)}>
                    <FiPlus className="me-2" />Schedule Meeting
                  </button>
                  <button className="btn btn-outline-primary" onClick={handleRefresh}>
                    {refreshing ? <div className="spinner-border spinner-border-sm" role="status"><span className="visually-hidden">Loading...</span></div> : <FiRefreshCw />}
                  </button>
                </div>
              </div>
              
              {error && (
                <div className="alert alert-danger alert-dismissible fade show" role="alert">
                  {error}
                  <button type="button" className="btn-close" onClick={() => setError(null)}></button>
                </div>
              )}
            </motion.div>

            {/* Stats Cards */}
            <motion.div variants={itemVariants} className="row mb-4">
              <div className="col-xl-3 col-md-6 mb-4">
                <div className="card stats-card border-0 shadow-sm h-100">
                  <div className="card-body">
                    <div className="d-flex align-items-center">
                      <div className="stats-icon stats-icon-primary me-3"><FiCalendar /></div>
                      <div className="flex-grow-1">
                        <h6 className="text-muted mb-1">Total Meetings</h6>
                        <h3 className="fw-bold mb-0">{meetings.length}</h3>
                      </div>
                    </div>
                    <div className="mt-3 d-flex align-items-center">
                      <span className="badge bg-success bg-opacity-10 text-success"><FiTrendingUp className="me-1" />12%</span>
                      <span className="text-muted ms-2 small">vs last month</span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-xl-3 col-md-6 mb-4">
                <div className="card stats-card border-0 shadow-sm h-100">
                  <div className="card-body">
                    <div className="d-flex align-items-center">
                      <div className="stats-icon stats-icon-success me-3"><FiVideo /></div>
                      <div className="flex-grow-1">
                        <h6 className="text-muted mb-1">Active</h6>
                        <h3 className="fw-bold mb-0">{meetings.filter(m => m.status === 'ongoing' || m.status === 'scheduled').length}</h3>
                      </div>
                    </div>
                    <div className="mt-3 d-flex align-items-center">
                      <span className="badge bg-success bg-opacity-10 text-success animate-pulse">Live/Scheduled</span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-xl-3 col-md-6 mb-4">
                <div className="card stats-card border-0 shadow-sm h-100">
                  <div className="card-body">
                    <div className="d-flex align-items-center">
                      <div className="stats-icon stats-icon-info me-3"><FiClock /></div>
                      <div className="flex-grow-1">
                        <h6 className="text-muted mb-1">Past</h6>
                        <h3 className="fw-bold mb-0">{meetings.filter(m => m.status === 'completed' || m.status === 'cancelled').length}</h3>
                      </div>
                    </div>
                    <div className="mt-3 d-flex align-items-center">
                      <span className="badge bg-secondary bg-opacity-10 text-secondary">History</span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-xl-3 col-md-6 mb-4">
                <div className="card stats-card border-0 shadow-sm h-100">
                  <div className="card-body">
                    <div className="d-flex align-items-center">
                      <div className="stats-icon stats-icon-secondary me-3"><FiVideoOff /></div>
                      <div className="flex-grow-1">
                        <h6 className="text-muted mb-1">Completed</h6>
                        <h3 className="fw-bold mb-0">{meetings.filter(m => m.status === 'completed').length}</h3>
                      </div>
                    </div>
                    <div className="mt-3 d-flex align-items-center">
                      <span className="badge bg-secondary bg-opacity-10 text-secondary">This week</span>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Tabs for filtering */}
            <motion.div variants={itemVariants} className="row mb-4">
              <div className="col-12">
                <div className="card border-0 shadow-sm">
                  <div className="card-body">
                    <ul className="nav nav-tabs nav-fill">
                      <li className="nav-item">
                        <button 
                          className={`nav-link ${activeTab === 'all' ? 'active' : ''}`} 
                          onClick={() => setActiveTab('all')}
                        >
                          All Meetings ({meetings.length})
                        </button>
                      </li>
                      <li className="nav-item">
                        <button 
                          className={`nav-link ${activeTab === 'active' ? 'active' : ''}`} 
                          onClick={() => setActiveTab('active')}
                        >
                          Active ({meetings.filter(m => m.status === 'ongoing' || m.status === 'scheduled').length})
                        </button>
                      </li>
                      <li className="nav-item">
                        <button 
                          className={`nav-link ${activeTab === 'past' ? 'active' : ''}`} 
                          onClick={() => setActiveTab('past')}
                        >
                          Past ({meetings.filter(m => m.status === 'completed' || m.status === 'cancelled').length})
                        </button>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Search and Filter */}
            <motion.div variants={itemVariants} className="row mb-4">
              <div className="col-12">
                <div className="card border-0 shadow-sm">
                  <div className="card-body">
                    <div className="row g-3">
                      <div className="col-md-4">
                        <div className="input-group">
                          <span className="input-group-text bg-white border-end-0"><FiSearch /></span>
                          <input 
                            type="text" 
                            className="form-control border-start-0" 
                            placeholder="Search by title, host..." 
                            value={searchTerm} 
                            onChange={(e) => setSearchTerm(e.target.value)} 
                          />
                        </div>
                      </div>
                      {showAllMeetings && (
                        <div className="col-md-3">
                          <input 
                            type="text" 
                            className="form-control" 
                            placeholder="Filter by host email..." 
                            value={hostFilter} 
                            onChange={(e) => setHostFilter(e.target.value)} 
                          />
                        </div>
                      )}
                      <div className={showAllMeetings ? "col-md-2" : "col-md-3"}>
                        <select className="form-select" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
                          <option value="all">All Status</option>
                          <option value="scheduled">Scheduled</option>
                          <option value="ongoing">Ongoing</option>
                          <option value="completed">Completed</option>
                          <option value="cancelled">Cancelled</option>
                        </select>
                      </div>
                      <div className={showAllMeetings ? "col-md-3" : "col-md-3"}>
                        <button className="btn btn-outline-secondary w-100"><FiFilter className="me-2" />More Filters</button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Meetings Table */}
            <motion.div variants={itemVariants} className="row">
              <div className="col-12">
                <div className="card border-0 shadow-sm">
                  <div className="card-header bg-white border-0 py-3">
                    <h5 className="mb-0 fw-bold">
                      {activeTab === 'all' && (showAllMeetings ? 'All Meetings' : 'My Meetings')}
                      {activeTab === 'active' && 'Active Meetings'}
                      {activeTab === 'past' && 'Past Meetings'}
                    </h5>
                  </div>
                  <div className="card-body p-0">
                    {loading ? (
                      <div className="d-flex justify-content-center align-items-center p-5">
                        <div className="spinner-border text-primary" role="status">
                          <span className="visually-hidden">Loading...</span>
                        </div>
                      </div>
                    ) : filteredMeetings.length === 0 ? (
                      <div className="text-center p-5">
                        <p className="text-muted">
                          {activeTab === 'active' ? 'No active meetings found.' : 
                           activeTab === 'past' ? 'No past meetings found.' : 
                           'No meetings found. Schedule your first meeting to get started.'}
                        </p>
                        <button className="btn btn-primary" onClick={() => setShowScheduleModal(true)}>
                          <FiPlus className="me-2" />Schedule Meeting
                        </button>
                      </div>
                    ) : (
                      <div className="table-responsive">
                        <table className="table table-hover mb-0">
                          <thead className="table-light">
                            <tr>
                              <th scope="col">Meeting</th>
                              <th scope="col">Host</th>
                              <th scope="col">Date & Time</th>
                              <th scope="col">Duration</th>
                              <th scope="col">Participants</th>
                              <th scope="col">Status</th>
                              <th scope="col" className="text-end">Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {currentMeetings.map((meeting) => (
                              <tr key={meeting.id}>
                                <td>
                                  <h6 className="mb-0 fw-medium">{meeting.title}</h6>
                                </td>
                                <td>{meeting.host}</td>
                                <td>{meeting.date} at {meeting.time}</td>
                                <td>{meeting.duration}</td>
                                <td>
                                  <div className="d-flex align-items-center">
                                    <FiUsers className="me-1" />
                                    <span>{meeting.participants}</span>
                                    {meeting.participants > 0 && (
                                      <button 
                                        className="btn btn-sm btn-outline-secondary ms-1" 
                                        onClick={() => handleViewMeeting(meeting)}
                                        title="View participants"
                                      >
                                        <FiEye />
                                      </button>
                                    )}
                                  </div>
                                </td>
                                <td>{getStatusBadge(meeting.status)}</td>
                                <td className="text-end">
                                  <div className="btn-group">
                                    <button className="btn btn-sm btn-outline-secondary" onClick={() => handleViewMeeting(meeting)}><FiEye /></button>
                                    {meeting.status === 'scheduled' && <button className="btn btn-sm btn-success" onClick={() => handleJoinMeeting(meeting)}>Start</button>}
                                    {meeting.status === 'ongoing' && <button className="btn btn-sm btn-success" onClick={() => handleJoinMeeting(meeting)}>Join</button>}
                                    {(meeting.status === 'scheduled' || meeting.status === 'ongoing') && <button className="btn btn-sm btn-outline-danger" onClick={() => handleCancelMeeting(meeting)}><FiXCircle /></button>}
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                  <div className="card-footer bg-white border-0 py-3 d-flex justify-content-between align-items-center">
                    <div className="text-muted small">Showing {indexOfFirstMeeting + 1} to {Math.min(indexOfLastMeeting, filteredMeetings.length)} of {filteredMeetings.length} meetings</div>
                    <div className="d-flex gap-1">
                      <button className="btn btn-sm btn-outline-secondary" onClick={() => setCurrentPage(currentPage > 1 ? currentPage - 1 : 1)} disabled={currentPage === 1}><FiChevronLeft /></button>
                      {[...Array(totalPages)].map((_, i) => <button key={i} className={`btn btn-sm ${currentPage === i + 1 ? 'btn-primary' : 'btn-outline-secondary'}`} onClick={() => setCurrentPage(i + 1)}>{i + 1}</button>)}
                      <button className="btn btn-sm btn-outline-secondary" onClick={() => setCurrentPage(currentPage < totalPages ? currentPage + 1 : totalPages)} disabled={currentPage === totalPages}><FiChevronRight /></button>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </Content>
      </div>

      {/* View Meeting Modal */}
      {showViewModal && selectedMeeting && (
        <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Meeting Details</h5>
                <button type="button" className="btn-close" onClick={() => setShowViewModal(false)}></button>
              </div>
              <div className="modal-body">
                <h4 className="mb-3">{selectedMeeting.title}</h4>
                <div className="row mb-3">
                  <div className="col-6"><p className="text-muted small mb-1">Host</p><p className="mb-0 fw-medium">{selectedMeeting.host}</p></div>
                  <div className="col-6"><p className="text-muted small mb-1">Status</p><p className="mb-0">{getStatusBadge(selectedMeeting.status)}</p></div>
                </div>
                <div className="row mb-3">
                  <div className="col-6"><p className="text-muted small mb-1">Date</p><p className="mb-0 fw-medium">{selectedMeeting.date}</p></div>
                  <div className="col-6"><p className="text-muted small mb-1">Time</p><p className="mb-0 fw-medium">{selectedMeeting.time}</p></div>
                </div>
                <div className="row mb-3">
                  <div className="col-6"><p className="text-muted small mb-1">Duration</p><p className="mb-0 fw-medium">{selectedMeeting.duration}</p></div>
                  <div className="col-6"><p className="text-muted small mb-1">Participants</p><p className="mb-0 fw-medium">{selectedMeeting.participants}</p></div>
                </div>
                {selectedMeeting.meetingLink && (
                  <div className="mb-3">
                    <p className="text-muted small mb-1">Meeting Link</p>
                    <div className="d-flex align-items-center">
                      <p className="mb-0 me-2 text-truncate">{selectedMeeting.meetingLink}</p>
                      <button className="btn btn-sm btn-outline-primary" onClick={() => navigator.clipboard.writeText(selectedMeeting.meetingLink)}>Copy</button>
                    </div>
                  </div>
                )}
                
                {selectedMeeting.participantsList && selectedMeeting.participantsList.length > 0 && (
                  <div className="mt-4">
                    <h5 className="mb-3">Participants</h5>
                    <div className="table-responsive">
                      <table className="table table-sm">
                        <thead>
                          <tr>
                            <th>Name</th>
                            <th>Email</th>
                            <th>Company</th>
                            <th>Role</th>
                          </tr>
                        </thead>
                        <tbody>
                          {selectedMeeting.participantsList.map((participant, index) => (
                            <tr key={index}>
                              <td>
                                <div className="d-flex align-items-center">
                                  <div className="user-avatar bg-primary text-white rounded-circle d-flex align-items-center justify-content-center me-2" style={{ width: '32px', height: '32px', fontSize: '14px' }}>
                                    {participant.full_name ? participant.full_name.split(' ').map(n => n[0]).join('').toUpperCase() : participant.email.substring(0, 2).toUpperCase()}
                                  </div>
                                  <span>{participant.full_name || participant.email}</span>
                                </div>
                              </td>
                              <td>{participant.email}</td>
                              <td>{participant.company_name || 'N/A'}</td>
                              <td>
                                {selectedMeeting.host === participant.email ? (
                                  <span className="badge bg-primary">Host</span>
                                ) : (
                                  <span className="badge bg-secondary">Participant</span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
                
                <div className="mb-3">
                  <p className="text-muted small mb-1">Agenda</p>
                  <p className="mb-0">1. Introduction<br />2. Main Discussion<br />3. Q&A Session<br />4. Summary & Next Steps</p>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowViewModal(false)}>Close</button>
                {selectedMeeting.status === 'ongoing' && <button type="button" className="btn btn-success" onClick={() => { setShowViewModal(false); handleJoinMeeting(selectedMeeting); }}>Join Meeting</button>}
                {selectedMeeting.status === 'scheduled' && <button type="button" className="btn btn-success" onClick={() => { setShowViewModal(false); handleJoinMeeting(selectedMeeting); }}>Start Meeting</button>}
                {selectedMeeting.meetingLink && <button type="button" className="btn btn-primary" onClick={() => { setShowViewModal(false); window.open(selectedMeeting.meetingLink, '_blank'); }}>Open Meeting</button>}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Schedule Meeting Modal */}
      {showScheduleModal && (
        <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Schedule New Meeting</h5>
                <button type="button" className="btn-close" onClick={() => setShowScheduleModal(false)}></button>
              </div>
              <div className="modal-body">
                <form onSubmit={(e) => { e.preventDefault(); createMeeting(); }}>
                  <div className="mb-3">
                    <label htmlFor="title" className="form-label">Meeting Title *</label>
                    <input 
                      type="text" 
                      className="form-control" 
                      id="title" 
                      placeholder="Enter meeting title" 
                      value={meetingTitle}
                      onChange={(e) => setMeetingTitle(e.target.value)}
                      required
                    />
                  </div>
                  <div className="mb-3">
                    <label htmlFor="date" className="form-label">Date</label>
                    <input 
                      type="date" 
                      className="form-control" 
                      id="date" 
                      value={meetingDate}
                      onChange={(e) => setMeetingDate(e.target.value)}
                    />
                  </div>
                  <div className="mb-3">
                    <label htmlFor="time" className="form-label">Time</label>
                    <input 
                      type="time" 
                      className="form-control" 
                      id="time" 
                      value={meetingTime}
                      onChange={(e) => setMeetingTime(e.target.value)}
                    />
                  </div>
                  <div className="mb-3">
                    <label htmlFor="duration" className="form-label">Duration</label>
                    <select 
                      className="form-select" 
                      id="duration"
                      value={meetingDuration}
                      onChange={(e) => setMeetingDuration(e.target.value)}
                    >
                      <option>15 minutes</option>
                      <option>30 minutes</option>
                      <option>45 minutes</option>
                      <option>1 hour</option>
                      <option>1.5 hours</option>
                      <option>2 hours</option>
                    </select>
                  </div>
                  <div className="mb-3">
                    <label htmlFor="status" className="form-label">Status</label>
                    <select 
                      className="form-select" 
                      id="status"
                      value={meetingStatus}
                      onChange={(e) => setMeetingStatus(e.target.value)}
                    >
                      <option value="scheduled">Scheduled</option>
                      <option value="ongoing">Ongoing</option>
                      <option value="completed">Completed</option>
                      <option value="cancelled">Cancelled</option>
                    </select>
                  </div>
                  <div className="mb-3">
                    <label htmlFor="participants" className="form-label">Participants</label>
                    <input 
                      type="text" 
                      className="form-control" 
                      id="participants" 
                      placeholder="Add participants (comma separated emails)" 
                      value={meetingParticipants}
                      onChange={(e) => setMeetingParticipants(e.target.value)}
                    />
                  </div>
                  <div className="d-flex justify-content-end">
                    <button type="button" className="btn btn-secondary me-2" onClick={() => setShowScheduleModal(false)}>Cancel</button>
                    <button type="submit" className="btn btn-primary" disabled={submitting}>
                      {submitting ? (
                        <div className="spinner-border spinner-border-sm" role="status">
                          <span className="visually-hidden">Loading...</span>
                        </div>
                      ) : 'Schedule Meeting'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Mobile Bottom Navigation */}
      <div className="d-md-none mobile-bottom-nav fixed-bottom bg-white shadow-lg">
        <div className="d-flex justify-content-around">
          <button className="btn btn-link text-dark"><i className="bi bi-house-door-fill fs-5"></i><small className="d-block">Dashboard</small></button>
          <button className="btn btn-link text-dark"><i className="bi bi-people fs-5"></i><small className="d-block">Members</small></button>
          <button className="btn btn-link text-dark"><i className="bi bi-calendar-event-fill fs-5"></i><small className="d-block">Meetings</small></button>
          <button className="btn btn-link text-dark"><i className="bi bi-gear fs-5"></i><small className="d-block">Settings</small></button>
        </div>
      </div>

      <style jsx>{`
        .app { display: flex; min-height: 100vh; position: relative; }
        .sidebar-wrapper { width: 250px; flex-shrink: 0; transition: transform 0.3s ease; }
        .main-content { flex-grow: 1; padding-bottom: 60px; }
        .mobile-header { position: fixed; top: 0; left: 0; right: 0; z-index: 1000; }
        .mobile-bottom-nav { height: 60px; z-index: 1000; }
        .mobile-menu-overlay { position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0,0,0,0.5); z-index: 999; }
        @media (max-width: 767.98px) { .sidebar-wrapper { position: fixed; top: 0; left: 0; bottom: 0; z-index: 1000; transform: translateX(-100%); } .sidebar-wrapper.mobile-open { transform: translateX(0); } .main-content { margin-left: 0; } }
        .stats-card { transition: all 0.3s ease; }
        .stats-card:hover { transform: translateY(-5px); box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1) !important; }
        .stats-icon { width: 48px; height: 48px; border-radius: 12px; display: flex; align-items: center; justify-content: center; font-size: 1.5rem; }
        .stats-icon-primary { background: rgba(99, 102, 241, 0.1); color: #6366f1; }
        .stats-icon-success { background: rgba(40, 167, 69, 0.1); color: #28a745; }
        .stats-icon-info { background: rgba(23, 162, 184, 0.1); color: #17a2b8; }
        .stats-icon-secondary { background: rgba(108, 117, 125, 0.1); color: #6c757d; }
        .table th { font-weight: 600; color: #495057; border-bottom: 1px solid #dee2e6; }
        .user-avatar { font-weight: 600; }
      `}</style>
    </div>
  )
}